// Cheap intent classifier that runs once before sendTurn when
// auto-routing is on. Burns one Haiku call (~$0.0005) so the heavy turn
// runs on the right-sized model, with thinking disabled by default, and
// against a slimmer system prompt where applicable.
//
// Why route in the client and not inside sendTurn:
//   - sendTurn is "execute one turn"; routing is "decide the profile
//     for the next turn". Keeping them separate makes the extra Haiku
//     call visible in ChatPanel (we surface the chosen profile in the
//     UI) and lets manual mode skip the routing call entirely.
//   - The router never holds a reference to the bridge; it's pure I/O
//     against the Anthropic API. Easy to test with a mocked SDK.

import Anthropic from '@anthropic-ai/sdk';
import type { ChatMessage } from './client';
import type { ModelId, EffortLevel } from './storage';
import type { SystemProfile } from './systemPrompt';

export type Intent = 'inspect' | 'edit' | 'compose';

export type RouteProfile = {
  intent: Intent;
  model: ModelId;
  effort: EffortLevel;
  systemProfile: SystemProfile;
  toolBudget: number;
  /** One-line rationale from the router — surfaced in the UI so the
   *  user understands why a turn ran cheap or expensive. */
  reason: string;
};

const ROUTER_MODEL: ModelId = 'claude-haiku-4-5';

const ROUTER_SYSTEM = `You classify a user's request to a Strudel music
coding agent into one of three intents. Output only the classify tool
call. Be terse.

Intents:
- inspect: read-only or pure Q&A. The user is asking what's in the
  song, what something means, or how Strudel works. Examples:
  "what's playing?", "what does lpf do?", "show me the bass voice",
  "explain this snippet".
- edit: surgical change to existing voices/effects. Examples: "lower
  the lpf on the bass", "swap the kick to bd*4", "humanize the hats",
  "transpose the lead down an octave", "add room to the pad".
- compose: meaningful creation, multi-voice arrangement, or large
  structural change. Examples: "write a 4-voice house groove",
  "arrange a breakdown", "compose a counter-melody", "build a
  techno track at 128 bpm with acid bass".

Bias toward the cheapest intent that fits. When in doubt between
edit and compose, choose edit.`;

const CLASSIFY_TOOL: Anthropic.Tool = {
  name: 'classify',
  description: 'Emit the intent classification.',
  input_schema: {
    type: 'object',
    properties: {
      intent: {
        type: 'string',
        enum: ['inspect', 'edit', 'compose'],
      },
      reason: {
        type: 'string',
        description: 'One short sentence — why this intent.',
      },
    },
    required: ['intent', 'reason'],
    additionalProperties: false,
  },
};

export type RouteOptions = {
  apiKey: string;
  userMessage: string;
  history: ChatMessage[];
  signal?: AbortSignal;
};

export async function routeIntent(opts: RouteOptions): Promise<RouteProfile> {
  const client = new Anthropic({
    apiKey: opts.apiKey,
    dangerouslyAllowBrowser: true,
  });
  // Last 4 messages (≈2 turns) is enough context for classification
  // without dragging the whole transcript into a Haiku call.
  const recent = opts.history.slice(-4);
  const result = await client.messages.create(
    {
      model: ROUTER_MODEL,
      max_tokens: 200,
      system: ROUTER_SYSTEM,
      tools: [CLASSIFY_TOOL],
      tool_choice: { type: 'tool', name: 'classify' },
      messages: [
        ...recent.map((m) => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: opts.userMessage },
      ],
    },
    { signal: opts.signal },
  );
  const block = result.content.find((b) => b.type === 'tool_use');
  if (block && block.type === 'tool_use') {
    const input = block.input as { intent?: unknown; reason?: unknown };
    if (
      (input.intent === 'inspect' ||
        input.intent === 'edit' ||
        input.intent === 'compose') &&
      typeof input.reason === 'string'
    ) {
      return profileFor(input.intent, input.reason);
    }
  }
  // Defensive fallback — Haiku occasionally drops the tool call when
  // it gets confused. 'edit' is a safe middle: cheaper than compose,
  // more capable than inspect.
  return profileFor('edit', 'router-fallback');
}

export function profileFor(intent: Intent, reason: string): RouteProfile {
  switch (intent) {
    case 'inspect':
      return {
        intent,
        model: 'claude-haiku-4-5',
        effort: 'off',
        systemProfile: 'minimal',
        toolBudget: 8,
        reason,
      };
    case 'edit':
      return {
        intent,
        model: 'claude-sonnet-4-6',
        effort: 'off',
        systemProfile: 'minimal',
        toolBudget: 15,
        reason,
      };
    case 'compose':
      return {
        intent,
        model: 'claude-opus-4-7',
        effort: 'low',
        systemProfile: 'compose',
        toolBudget: 30,
        reason,
      };
  }
}
